import { Button, Space, Tag, Tooltip, Typography } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { MdCheckCircle, MdEmail, MdOpenInNew, MdPhone } from 'react-icons/md';
import { toast } from 'react-hot-toast';

import dayjs from 'dayjs';
import { Table } from '@/components/table';
import { loadingTableHandler } from '@/utils/handler/loadingTableHandler';
import { useQuoteFollowups, useUpdateQuoteFollowup } from '../hooks';
import { QuoteFollowup } from '../types';
import { CompleteFollowUpModal } from './CompleteFollowUpModal';
import { useUpdateQuote } from '@/features/quotes-management';
import { QuoteFollowUpStatus } from '@/features/quotes-management/types';

const { Text } = Typography;

interface DashboardFollowUpTableProps {
  showRegion?: boolean;
  canManageFollowup?: boolean;
  regionFilter?: string;
}

const getUrgency = (followupDate?: string | null) => {
  const now = new Date();
  if (!followupDate) {
    return { daysSince: 0, label: 'No Date', color: 'default' as const };
  }

  const targetDate = new Date(followupDate);
  const diffTime = targetDate.getTime() - now.getTime();
  const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (daysUntil < 0) {
    return { daysSince: Math.abs(daysUntil), label: 'Overdue', color: 'error' as const };
  }
  if (daysUntil <= 1) {
    return { daysSince: daysUntil, label: 'Today/Tomorrow', color: 'warning' as const };
  }
  if (daysUntil <= 3) {
    return { daysSince: daysUntil, label: 'This Week', color: 'processing' as const };
  }
  return { daysSince: daysUntil, label: 'Later', color: 'success' as const };
};

export const DashboardFollowUpTable: React.FC<DashboardFollowUpTableProps> = ({
  canManageFollowup = false,
  regionFilter,
}) => {
  const { data: followupsData, isLoading } = useQuoteFollowups(regionFilter);
  const followups = followupsData?.value || [];
  const updateFollowupMutation = useUpdateQuoteFollowup();
  const updateQuoteMutation = useUpdateQuote();
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [selectedFollowup, setSelectedFollowup] = useState<QuoteFollowup | null>(null);

  const onPhoneClick = useCallback((record: QuoteFollowup) => {
    const customer = record.dogma_Quote?.customerid_account || record.dogma_Quote?.customerid_contact;
    const phone = customer && 'telephone1' in customer ? customer.telephone1 : null;

    if (phone) {
      window.location.href = `tel:${phone}`;
    } else {
      toast.error('No phone number available for this customer');
    }
  }, []);

  const onEmailClick = useCallback((record: QuoteFollowup) => {
    const customer = record.dogma_Quote?.customerid_account || record.dogma_Quote?.customerid_contact;
    const email = customer && 'emailaddress1' in customer ? customer.emailaddress1 : null;

    if (email) {
      const subject = encodeURIComponent(`Follow Up: ${record.dogma_Quote?.name || 'Quote'}`);
      const body = encodeURIComponent(`Hi,\n\nFollowing up on quote ${record.dogma_Quote?.quotenumber || ''} regarding ${record.dogma_name}.\n\nBest regards`);
      window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    } else {
      toast.error('No email address available for this customer');
    }
  }, []);

  const onMarkComplete = useCallback((record: QuoteFollowup) => {
    setSelectedFollowup(record);
    setCompleteModalOpen(true);
  }, []);

  const handleCompleteConfirm = async () => {
    if (!selectedFollowup) return;

    try {
      await updateFollowupMutation.mutateAsync({
        id: selectedFollowup.dogma_quotefollowupid,
        statecode: 1,
        statuscode: 2,
      });
      const quoteId = selectedFollowup.dogma_Quote?.quoteid ?? selectedFollowup._dogma_quote_value;
      if (quoteId) {
        await updateQuoteMutation.mutateAsync({
          id: quoteId,
          payload: {
            dogma_followup: QuoteFollowUpStatus.RESOLVED,
          },
        });
      }
      toast.success('Follow-up marked as complete successfully');
      setCompleteModalOpen(false);
      setSelectedFollowup(null);
    } catch (error) {
      toast.error('Failed to mark follow-up as complete');
    }
  };

  const handleCompleteCancel = () => {
    setCompleteModalOpen(false);
    setSelectedFollowup(null);
  };

  const columns = useMemo(() => {
    return [
      {
        title: 'Urgency',
        dataIndex: 'dogma_followupdate',
        key: 'urgency',
        width: 150,
        render: (_: unknown, record: QuoteFollowup) => {
          const urgency = getUrgency(record.dogma_followupdate);
          return (
            <Tag color={urgency.color}>
              {record.dogma_followupdate ?
                `${urgency.daysSince}d - ${urgency.label}` :
                urgency.label}
            </Tag>
          );
        },
      },
      {
        title: 'Follow Up Type',
        dataIndex: 'dogma_name',
        width: 180,
        render: (_: string, record: QuoteFollowup) => (
          <div className="flex flex-col">
            <Text strong>{record.dogma_name}</Text>
            <Text type="secondary" className="text-xs">
              {record['dogma_type@OData.Community.Display.V1.FormattedValue'] || 'N/A'}
            </Text>
          </div>
        ),
      },
      {
        title: 'Quote',
        dataIndex: 'dogma_Quote',
        width: 200,
        render: (_: unknown, record: QuoteFollowup) => (
          <div className="flex flex-col">
            <Text strong>{record.dogma_Quote?.name || 'N/A'}</Text>
            <Text type="secondary" className="text-xs">
              {record.dogma_Quote?.quotenumber || 'N/A'}
            </Text>
          </div>
        ),
      },
      {
        title: 'Customer',
        dataIndex: 'dogma_Quote',
        width: 220,
        render: (_: unknown, record: QuoteFollowup) => {
          const customer = record.dogma_Quote?.customerid_account || record.dogma_Quote?.customerid_contact;
          const name = customer && 'name' in customer ? customer.name : customer && 'fullname' in customer ? customer.fullname : 'N/A';
          return (
            <div className="flex flex-col">
              <Text strong>{name}</Text>
            </div>
          );
        },
      },
      {
        title: 'Quote Value',
        dataIndex: 'dogma_Quote',
        width: 130,
        align: 'right' as const,
        render: (_: unknown, record: QuoteFollowup) => (
          <Text strong>
            {record.dogma_Quote?.['totalamount@OData.Community.Display.V1.FormattedValue'] || '-'}
          </Text>
        ),
      },
      {
        title: 'Follow Up Date',
        dataIndex: 'dogma_followupdate',
        width: 150,
        // Our own en-GB formatter — Dataverse's own @OData...FormattedValue
        // is formatted per the org/user's locale (e.g. en-US), not fixed to
        // UK format.
        render: (date: string | null | undefined) => (
          <Text>{date ? dayjs(date).format('DD/MM/YYYY') : 'Not Set'}</Text>
        ),
      },
      {
        title: 'Notes',
        dataIndex: 'dogma_notes',
        width: 200,
        ellipsis: true,
        render: (notes: string | undefined | null) => (
          <Tooltip title={notes || '-'}>
            <Text className="text-xs" type="secondary">{notes || '-'}</Text>
          </Tooltip>
        ),
      },
      {
        title: 'Created Date',
        dataIndex: 'createdon',
        width: 150,
        render: (date: string | undefined) => (
          <Text className="text-xs">{date ? dayjs(date).format('DD/MM/YYYY') : 'N/A'}</Text>
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 200,
        align: 'center' as const,
        render: (_: unknown, record: QuoteFollowup) => {
          const followupType = record.dogma_type;
          const showPhone = followupType === 1 || followupType === 3;
          const showEmail = followupType === 1 || followupType === 2;

          return (
            <Space>
              {/* Management actions - require manage_followup permission */}
              {canManageFollowup && showPhone && (
                <Tooltip title="Call Customer">
                  <Button
                    type="primary"
                    icon={<MdPhone size={16} />}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPhoneClick(record);
                    }}
                    style={{ width: 32, height: 32, padding: 0 }}
                  />
                </Tooltip>
              )}
              {canManageFollowup && showEmail && (
                <Tooltip title="Email Customer">
                  <Button
                    icon={<MdEmail size={16} />}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEmailClick(record);
                    }}
                    style={{ width: 32, height: 32, padding: 0 }}
                  />
                </Tooltip>
              )}
              {canManageFollowup && (
                <Tooltip title="Mark as Followed Up">
                  <Button
                    type="dashed"
                    icon={<MdCheckCircle size={16} />}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMarkComplete(record);
                    }}
                    style={{ width: 32, height: 32, padding: 0 }}
                  />
                </Tooltip>
              )}
              {/* View action - always available when you can view follow-ups */}
              <Tooltip title="View Quote Details">
                <Button
                  icon={<MdOpenInNew size={16} />}
                  size="small"
                  disabled={!record._dogma_quote_value}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (record._dogma_quote_value) {
                      window.location.href = `/quotes/${record._dogma_quote_value}`;
                    }
                  }}
                  style={{ width: 32, height: 32, padding: 0 }}
                />
              </Tooltip>
            </Space>
          );
        },
      },
    ];
  }, [onPhoneClick, onEmailClick, onMarkComplete, canManageFollowup]);

  return (
    <div className="bg-white  rounded-lg ">
      <div className="flex items-center px-4 py-3 justify-between">
        <div>
          <Text strong className="text-base">
            Follow-Up Queue
          </Text>
          <div className="text-xs text-gray-500">Quotes needing follow-up</div>
        </div>
        <Tag color="blue">{followups.length} follow-ups</Tag>
      </div>
      {
        loadingTableHandler(isLoading, <Table<QuoteFollowup>
          isLoading={isLoading}
          columns={columns}
          dataSource={followups}
          rowKey="dogma_quotefollowupid"
          showPagination={false}
        />)
      }

      <CompleteFollowUpModal
        open={completeModalOpen}
        onClose={handleCompleteCancel}
        onConfirm={handleCompleteConfirm}
        isLoading={updateFollowupMutation.isPending || updateQuoteMutation.isPending}
        followUpName={selectedFollowup?.dogma_name}
      />
    </div>
  );
};

export default DashboardFollowUpTable;

